import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import FractionNodeView from '../components/FractionNodeView';
import SqrtNodeView from '../components/SqrtNodeView';
import PowerNodeView from '../components/PowerNodeView';

/**
 * فرمول‌های درون‌خطی قابل‌تایپ مستقیم در ادیتور (مثل Equation ورد):
 * - fraction      : کسر (صورت/مخرج)
 * - mixedFraction : عدد مخلوط (عدد صحیح + کسر)
 * - sqrt          : رادیکال ساده
 * - nthRoot       : رادیکال با فرجه
 * - power         : توان (پایه + توان)
 * - subscript     : اندیس (پایه + اندیس)
 *
 * هر نود هنگام ذخیره به معادل LaTeX خود تبدیل می‌شود (data-latex) و
 * در سایت اصلی/چاپ با همان KaTeX رندر می‌شود.
 */

const makeLatex = (name, attrs) => {
  const v = (s) => (s || '').trim();
  switch (name) {
    case 'fraction':
      return `\\dfrac{${v(attrs.num)}}{${v(attrs.den)}}`;
    case 'mixedFraction':
      return `${v(attrs.whole)}\\,\\dfrac{${v(attrs.num)}}{${v(attrs.den)}}`;
    case 'sqrt':
      return `\\sqrt{${v(attrs.radicand)}}`;
    case 'nthRoot':
      return `\\sqrt[${v(attrs.degree)}]{${v(attrs.radicand)}}`;
    case 'power':
      return `{${v(attrs.base)}}^{${v(attrs.exp)}}`;
    case 'subscript':
      return `{${v(attrs.base)}}_{${v(attrs.sub)}}`;
    default:
      return '';
  }
};

const createFormulaNode = ({ name, fields, nodeView }) =>
  Node.create({
    name,
    group: 'inline',
    inline: true,
    atom: true,
    marks: '_',

    addAttributes() {
      return Object.fromEntries(
        fields.map((f) => [
          f,
          {
            default: '',
            // HTML خام ادیتور: data-num و... — خروجی renderHTML: اتریبیوت خام (num=)
            // هر دو فرمت خوانده می‌شوند تا سوال ذخیره‌شده هم قابل ویرایش مجدد باشد
            parseHTML: (element) =>
              element.getAttribute(`data-${f}`) || element.getAttribute(f) || '',
          },
        ])
      );
    },

    parseHTML() {
      return [{ tag: `span[data-type="${name}"]` }];
    },

    renderHTML({ node, HTMLAttributes }) {
      return [
        'span',
        mergeAttributes(HTMLAttributes, {
          'data-type': name,
          'data-latex': makeLatex(name, node.attrs),
          class: 'math-formula-container',
          style:
            'display: inline-block; direction: ltr; unicode-bidi: isolate; vertical-align: middle;',
        }),
        // خروجی استاتیک برای دیتابیس/چاپ — KaTeX آن را رندر می‌کند
        ['span', { class: 'inline-formula-static' }, makeLatex(name, node.attrs)],
      ];
    },

    addNodeView() {
      return ReactNodeViewRenderer(nodeView);
    },

    addCommands() {
      const commandName =
        name === 'fraction' ? 'insertFraction'
        : name === 'mixedFraction' ? 'insertMixedFraction'
        : name === 'sqrt' ? 'insertSqrt'
        : name === 'power' ? 'insertPower'
        : name === 'subscript' ? 'insertSubscript'
        : 'insertNthRoot';
      return {
        [commandName]:
          () =>
          ({ chain }) =>
            chain()
              .insertContent({ type: name, attrs: Object.fromEntries(fields.map((f) => [f, ''])) })
              .run(),
      };
    },
  });

export const Fraction = createFormulaNode({
  name: 'fraction',
  fields: ['num', 'den'],
  nodeView: FractionNodeView,
});

export const MixedFraction = createFormulaNode({
  name: 'mixedFraction',
  fields: ['whole', 'num', 'den'],
  nodeView: FractionNodeView,
});

export const Sqrt = createFormulaNode({
  name: 'sqrt',
  fields: ['radicand'],
  nodeView: SqrtNodeView,
});

export const NthRoot = createFormulaNode({
  name: 'nthRoot',
  fields: ['degree', 'radicand'],
  nodeView: SqrtNodeView,
});

export const Power = createFormulaNode({
  name: 'power',
  fields: ['base', 'exp'],
  nodeView: PowerNodeView,
});

export const Subscript = createFormulaNode({
  name: 'subscript',
  fields: ['base', 'sub'],
  nodeView: PowerNodeView,
});

/** فرمان‌های درج به صورت خودکار داخل هر اکستنشن ثبت می‌شوند (addCommands بالا) */
